import { createRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import z from 'zod';
import {
  INPATIENT_INFO_LAYOUT_ROUTE_ID,
  INPATIENT_INFO_ROUTES,
  PATIENT_360_ROUTES,
} from '@/features/patient-360/consts/routes';
import { patient360ViewRoute } from '@/features/patient-360/patient-360-view-route';
import {
  reportSearchParams,
  sharedSearchParams,
} from '@/features/patient-360/types/route-schema';
import { beforeLoadRouterGuard } from '@/integrations/tanstack-router/before-load-router-guard';
import { relativeTo } from '@/integrations/tanstack-router/relative-to';

const CHILD_STATIC_DATA = {
  parent: INPATIENT_INFO_ROUTES.inpatientInfo,
  home: PATIENT_360_ROUTES.patientView,
} as const;

const inpatientInfoLayout = createRoute({
  id: INPATIENT_INFO_LAYOUT_ROUTE_ID,
  getParentRoute: () => patient360ViewRoute,
  beforeLoad: beforeLoadRouterGuard([]),
  validateSearch: zodValidator(
    sharedSearchParams.extend({
      encounterId: z.string().optional(),
      categoryName: z.string().optional(),
      ...reportSearchParams.shape,
    }),
  ),
}).lazy(() =>
  import('./components/inpatient-info/inpatient-info-layout').then(
    ({ Route }) => Route,
  ),
);
const inpatientInfoIndexRoute = createRoute({
  getParentRoute: () => inpatientInfoLayout,
  staticData: {
    parent: PATIENT_360_ROUTES.visits,
    home: PATIENT_360_ROUTES.patientView,
  },
  path: relativeTo(
    INPATIENT_INFO_ROUTES.inpatientInfo,
    PATIENT_360_ROUTES.patientView,
  ),
}).lazy(() =>
  import('./components/inpatient-info/inpatient-info').then(
    ({ Route }) => Route,
  ),
);

const dischargeMedicationsRoute = createRoute({
  getParentRoute: () => inpatientInfoLayout,
  staticData: CHILD_STATIC_DATA,
  path: relativeTo(
    INPATIENT_INFO_ROUTES.dischargeMedications,
    PATIENT_360_ROUTES.patientView,
  ),
}).lazy(() =>
  import('./components/inpatient-info/discharge-medications').then(
    ({ Route }) => Route,
  ),
);

const dischargeNoteRoute = createRoute({
  getParentRoute: () => inpatientInfoLayout,
  staticData: CHILD_STATIC_DATA,
  path: relativeTo(
    INPATIENT_INFO_ROUTES.dischargeNote,
    PATIENT_360_ROUTES.patientView,
  ),
}).lazy(() =>
  import('./components/inpatient-info/discharge-note').then(
    ({ Route }) => Route,
  ),
);

const surgeryRecordsRoute = createRoute({
  getParentRoute: () => inpatientInfoLayout,
  staticData: CHILD_STATIC_DATA,
  path: relativeTo(
    INPATIENT_INFO_ROUTES.surgeryRecords,
    PATIENT_360_ROUTES.patientView,
  ),
}).lazy(() =>
  import('./components/inpatient-info/surgery-records').then(
    ({ Route }) => Route,
  ),
);

const labReportsRoute = createRoute({
  getParentRoute: () => inpatientInfoLayout,
  staticData: CHILD_STATIC_DATA,
  path: relativeTo(
    INPATIENT_INFO_ROUTES.labReports,
    PATIENT_360_ROUTES.patientView,
  ),
}).lazy(() =>
  import('./components/inpatient-info/lab-reports').then(({ Route }) => Route),
);

const imagingReportsRoute = createRoute({
  getParentRoute: () => inpatientInfoLayout,
  staticData: CHILD_STATIC_DATA,
  path: relativeTo(
    INPATIENT_INFO_ROUTES.imagingReports,
    PATIENT_360_ROUTES.patientView,
  ),
}).lazy(() =>
  import('./components/inpatient-info/imaging-reports').then(
    ({ Route }) => Route,
  ),
);

const examReportsRoute = createRoute({
  getParentRoute: () => inpatientInfoLayout,
  staticData: CHILD_STATIC_DATA,
  path: relativeTo(
    INPATIENT_INFO_ROUTES.examReports,
    PATIENT_360_ROUTES.patientView,
  ),
}).lazy(() =>
  import('./components/inpatient-info/exam-reports').then(({ Route }) => Route),
);

const pathologyReportsRoute = createRoute({
  getParentRoute: () => inpatientInfoLayout,
  staticData: CHILD_STATIC_DATA,
  path: relativeTo(
    INPATIENT_INFO_ROUTES.pathologyReports,
    PATIENT_360_ROUTES.patientView,
  ),
}).lazy(() =>
  import('./components/inpatient-info/pathology-reports').then(
    ({ Route }) => Route,
  ),
);

const admissionNoteRoute = createRoute({
  getParentRoute: () => inpatientInfoLayout,
  staticData: CHILD_STATIC_DATA,
  path: relativeTo(
    INPATIENT_INFO_ROUTES.admissionNote,
    PATIENT_360_ROUTES.patientView,
  ),
}).lazy(() =>
  import('./components/inpatient-info/admission-note').then(
    ({ Route }) => Route,
  ),
);

const progressNotesRoute = createRoute({
  getParentRoute: () => inpatientInfoLayout,
  staticData: CHILD_STATIC_DATA,
  path: relativeTo(
    INPATIENT_INFO_ROUTES.progressNotes,
    PATIENT_360_ROUTES.patientView,
  ),
}).lazy(() =>
  import('./components/inpatient-info/progress-notes').then(
    ({ Route }) => Route,
  ),
);

const nursingNotesRoute = createRoute({
  getParentRoute: () => inpatientInfoLayout,
  staticData: CHILD_STATIC_DATA,
  path: relativeTo(
    INPATIENT_INFO_ROUTES.nursingNotes,
    PATIENT_360_ROUTES.patientView,
  ),
}).lazy(() =>
  import('./components/inpatient-info/nursing-notes').then(
    ({ Route }) => Route,
  ),
);

const ordersRoute = createRoute({
  getParentRoute: () => inpatientInfoLayout,
  staticData: CHILD_STATIC_DATA,
  path: relativeTo(
    INPATIENT_INFO_ROUTES.orders,
    PATIENT_360_ROUTES.patientView,
  ),
}).lazy(() =>
  import('./components/inpatient-info/orders').then(({ Route }) => Route),
);

const tprRoute = createRoute({
  getParentRoute: () => inpatientInfoLayout,
  staticData: CHILD_STATIC_DATA,
  path: relativeTo(INPATIENT_INFO_ROUTES.tpr, PATIENT_360_ROUTES.patientView),
}).lazy(() =>
  import('./components/inpatient-info/tpr').then(({ Route }) => Route),
);

const reportContentRoute = createRoute({
  getParentRoute: () => inpatientInfoLayout,
  staticData: CHILD_STATIC_DATA,
  path: relativeTo(
    INPATIENT_INFO_ROUTES.reportContent,
    PATIENT_360_ROUTES.patientView,
  ),
}).lazy(() =>
  import('./components/inpatient-info/report-content').then(
    ({ Route }) => Route,
  ),
);

const labReportDateGroupRoute = createRoute({
  getParentRoute: () => inpatientInfoLayout,
  staticData: CHILD_STATIC_DATA,
  path: relativeTo(
    INPATIENT_INFO_ROUTES.labReportDateGroup,
    PATIENT_360_ROUTES.patientView,
  ),
}).lazy(() =>
  import('./components/inpatient-info/lab-reports/lab-report-date-group').then(
    ({ Route }) => Route,
  ),
);
const labReportCategoryGroupRoute = createRoute({
  getParentRoute: () => inpatientInfoLayout,
  staticData: CHILD_STATIC_DATA,
  path: relativeTo(
    INPATIENT_INFO_ROUTES.labReportCategoryGroup,
    PATIENT_360_ROUTES.patientView,
  ),
}).lazy(() =>
  import(
    './components/inpatient-info/lab-reports/lab-report-category-group'
  ).then(({ Route }) => Route),
);

export const inpatientInfoRoutes = inpatientInfoLayout.addChildren([
  inpatientInfoIndexRoute,
  dischargeMedicationsRoute,
  dischargeNoteRoute,
  surgeryRecordsRoute,
  labReportsRoute,
  labReportDateGroupRoute,
  labReportCategoryGroupRoute,
  imagingReportsRoute,
  examReportsRoute,
  pathologyReportsRoute,
  admissionNoteRoute,
  progressNotesRoute,
  nursingNotesRoute,
  ordersRoute,
  tprRoute,
  reportContentRoute,
]);
